import React, { useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Button, colors, Grid, Typography } from '@mui/material'
import { orderBy } from 'lodash'
import { statusColors } from '@shared/utils/status-colors'
import { useStatusesQuery } from '@shared/hooks/use-statuses-query.hook'
import { StatusTypes } from '@shared/types'
import { useFormContext } from 'react-hook-form'

type Props = {
  title: string
  editable?: boolean
  isClient?: boolean
}

export const FormStatusSelection = ({ title, isClient, editable }: Props) => {
  const classes = useStyles()
  const { areStatusesLoading, statuses } = useStatusesQuery()
  const { watch, setValue } = useFormContext()
  const statusID = watch(isClient ? 'clientStatusID' : 'auditorStatusID')

  const statusesSorted = useMemo(
    () => orderBy(statuses ?? [], 'order', 'asc'),
    [statuses]
  )

  const onHandleStatusChange = (statusID: number) => () => {
    setValue(isClient ? 'clientStatusID' : 'auditorStatusID', statusID, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  if (areStatusesLoading) {
    return null
  }

  return (
    <>
      <Grid container className={classes.textContainer}>
        <Typography variant="h6">{title}</Typography>
      </Grid>
      <Grid
        container
        justifyContent="space-between"
        className={classes.statusButtonsContainer}
      >
        {statusesSorted?.map((status) => {
          const isSelected = statusID === status.statusID

          const statusStyle = {
            backgroundColor: isSelected ? statusColors?.[status.label] : '',
            color: isSelected
              ? colors.common.white
              : statusColors?.[status.label],
            borderColor: isSelected ? '' : statusColors?.[status.label],
          }
          const isButtonDisabled = isClient
            ? ![StatusTypes.Accepted, StatusTypes.NotAssessed].includes(
                status.label
              )
            : [StatusTypes.New].includes(status.label)

          return (
            <Button
              disabled={!editable || isButtonDisabled}
              key={status.statusID}
              style={statusStyle}
              className={classes.statusButton}
              onClick={onHandleStatusChange(status.statusID)}
              data-status-id={status.statusID}
            >
              {status.name}
            </Button>
          )
        })}
      </Grid>
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    container: {
      padding: theme.spacing(1),
    },
    title: {
      textTransform: 'uppercase',
      marginBottom: theme.spacing(1),
    },
    breadcrumbs: {
      color: theme.palette.grey['600'],
      textTransform: 'uppercase',
      marginBottom: theme.spacing(2),
    },
    status: {
      color: theme.palette.grey['700'],
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: theme.spacing(0.25),
      marginRight: theme.spacing(1),
    },
    statusContainer: {
      display: 'flex',
    },
    legalBasis: {
      marginLeft: theme.spacing(5),
      marginTop: theme.spacing(1),
    },
    statusButton: {
      '&.Mui-disabled': {
        opacity: 0.5,
      },
      textTransform: 'uppercase',
      border: '1px solid',
      width: 140,
      marginBottom: theme.spacing(2),
    },
    textContainer: {
      paddingTop: theme.spacing(1),
    },
    statusButtonsContainer: {
      marginTop: theme.spacing(1),
      height: 'flex',
    },
  }
})
